import {
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Component, inject, OnDestroy, OnInit, TemplateRef, viewChild } from '@angular/core';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { MatSnackBarDirective } from 'src/app/shared/directives/mat-snackbar.directive';
import { AuthService } from '../../services/auth.service';
import { EMPTY, Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { unsetLoading } from 'src/app/loading/state/actions/loading.actions';
import { requiresChecked } from 'src/app/shared/utils/shared';
import { Mongo } from 'src/app/core/models/mongo';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';

// Declare gTM dataLayer array.
declare global {
  interface Window { dataLayer: any[]; }
}

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatCheckbox,
    MatButton,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    TranslateModule
  ],
  styles: [`
    @use '@angular/material' as mat;

    * {
      --mat-checkbox-label-text-size: 0.8rem;
    }

    .smaller {
      font-size: 0.8rem;
    }

    .dense-zone {
      @include mat.input-density(2);
    }

    :host ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      display: none;
    }

    .pull-up {
      margin-top: -12px;
    }
  `]
})
export class SignupComponent extends MatSnackBarDirective implements OnInit, OnDestroy {
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private authSrv = inject(AuthService);
  private fb = inject(UntypedFormBuilder);
  private readonly translateService = inject(TranslateService);
  private store = inject(Store);

  singupForm!: UntypedFormGroup;

  readonly errorDialog = viewChild.required<TemplateRef<any>>('errorDialog');
  readonly emailAlreadyExists = viewChild.required<TemplateRef<any>>('emailAlreadyExists');
  readonly tenantAlreadyExists = viewChild.required<TemplateRef<any>>('tenantAlreadyExists');

  private sub = new Subscription();

  ngOnInit(): void {
    this.singupForm = this.fb.group({
      tenant: ['', Validators.required],
      email: ['', Validators.required],
      termsAndConditions: [false, requiresChecked()],
      unfairTermsAndConditions: [false, requiresChecked()],
      serviceAndContractFulfillment: [false, requiresChecked()],
      strategicPlanningAndAnalytics: false,
      directMarketing: false,
    });

    const changesSub = this.singupForm.valueChanges.pipe(take(1)).subscribe(() => {
      if (window.dataLayer) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'registration_started', platform: 'platform' });
      }
    });

    this.sub.add(changesSub);
  }

  signup(): any {
    const {
      email,
      tenant,
      termsAndConditions,
      unfairTermsAndConditions,
      serviceAndContractFulfillment,
      strategicPlanningAndAnalytics,
      directMarketing,
    } = this.singupForm.getRawValue();


    this.authSrv
      .signup(this.normalizeEmail(email), this.normalizeTenant(tenant), {
        termsAndConditions: termsAndConditions,
        unfairTermsAndConditions: unfairTermsAndConditions,
        serviceAndContractFulfillment: serviceAndContractFulfillment,
        strategicPlanningAndAnalytics: strategicPlanningAndAnalytics,
        directMarketing,
        timestamp: new Mongo.Date(),
      })
      .pipe(
        catchError((error: any) => {
          this.store.dispatch(unsetLoading());
          if (error.status === 409) {
            return this.dialog
              .open(this.emailAlreadyExists())
              .afterClosed()
              .pipe(switchMap(() => EMPTY));
          } else if (error.status === 403) {
            return this.dialog
              .open(this.tenantAlreadyExists())
              .afterClosed()
              .pipe(switchMap(() => EMPTY));
          } else {
            console.error(error);
            return this.dialog
              .open(this.errorDialog())
              .afterClosed()
              .pipe(switchMap(() => EMPTY));
          }
        }),
        tap(() => {
          this.openSnackbar(this.translateService.instant('signup.component.success'));
          this.router.navigate(['signin']);
          this.router.navigate(['', 'signin', 'check-your-email']);
        }),
        tap(() => {
          if (window.dataLayer) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'registration_completed', platform: 'platform' });
          }
        })
      )
      .subscribe();
  }

  private normalizeEmail(email: string): string {
    return email.toLowerCase().trim();
  }

  /**
   * Normalize the tenant name to be used as id
   *
   * @param tenantName the tenant name
   * @return the normalized id limited to 48 characters
   */
  normalizeTenant(tenantName: string): string {
    let result = tenantName.trim();

    // If the input string is already normalized, return it as is
    if (/^[a-z0-9_]+$/.test(result)) {
        return result.length > 48 ? result.substring(0, 48) : result;
    }

    // Replace multiple spaces with a single space
    result = tenantName.trim().replace(/\s+/g, " ");

    // Replace punctuation characters with empty strings and - with *
    // First remove all punctuation except hyphens
    result = result.toLowerCase().replace(/[!@#$%^&*()_+={}\[\]:;"'<>,.?/\\|~`]/g, "");
    // Then replace hyphens with asterisks
    result = result.replace(/-/g, "*");

    // Replace all non-alphabetic characters with a single underscore
    result = result.replace(/[^\p{L}\p{Nd}_]/gu, "_");

    // Limit to 48 characters
    return result.length > 48 ? result.substring(0, 48) : result;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
